import { notify } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, BookOpen, Plus, Trash2, Pencil, Archive,
  PackageOpen, Factory, Save, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@/lib/utils';

interface RecipeProduct {
  id?: string;
  name?: string;
  costPrice?: number | string | null;
  stockQuantity?: number | string | null;
}

interface RecipeItem {
  id: string;
  quantity: number | string;
  uom?: string | null;
  notes?: string | null;
  lineTotal?: number | string | null;
  standardCost?: number | string | null;
  rawMaterial?: RecipeProduct | null;
  rawMaterialProduct?: RecipeProduct | null;
  product?: RecipeProduct | null;
  rawMaterialProductId?: string | null;
  productId?: string | null;
}

interface RawMaterialOption {
  id: string;
  name: string;
  stockQuantity?: number | string | null;
}

interface AddRecipeItemBody {
  productId: string;
  quantity: number;
  uom?: string;
  notes?: string;
}

interface UpdateRecipeItemBody {
  quantity: number;
  uom?: string;
  notes?: string;
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:     'bg-success/10 text-success-text',
  DRAFT:      'bg-warning/10 text-warning-text',
  ARCHIVED:   'bg-muted text-muted-foreground',
  DEPRECATED: 'bg-destructive/10 text-destructive-text',
};

export function RecipeDetailPage() {
  const { t } = useTranslation('restaurant');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('recipes:update') || hasPermission('recipes:*') || hasPermission('*');

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ productId: '', quantity: '1', uom: '', notes: '' });
  const [editingItem, setEditingItem] = useState<RecipeItem | null>(null);

  const { data: recipe, isLoading } = useQuery({
    queryKey: ['recipe', id],
    queryFn: () => api.get(`/recipes/${id}`).then(r => r.data),
    enabled: !!id,
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-raw', id],
    queryFn: () => api.get('/products', { params: { limit: 200, isRawMaterial: true } }).then(r => r.data),
    enabled: addItemOpen || !!editingItem,
  });

  const addItemMutation = useMutation({
    mutationFn: (body: AddRecipeItemBody) => api.post(`/recipes/${id}/items`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipe', id] });
      toast.success('Ingredient added');
      setAddItemOpen(false);
      setItemForm({ productId: '', quantity: '1', uom: '', notes: '' });
    },
    onError: (e) => notify.error(e, 'Failed to add ingredient'),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: UpdateRecipeItemBody }) =>
      api.patch(`/recipes/${id}/items/${itemId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipe', id] });
      toast.success('Ingredient updated');
      setEditingItem(null);
    },
    onError: (e) => notify.error(e, 'Failed to update ingredient'),
  });

  const removeItemMutation = useMutation({
    mutationFn: (itemId: string) => api.delete(`/recipes/${id}/items/${itemId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recipe', id] }); toast.success('Ingredient removed'); },
    onError: () => toast.error('Failed to remove ingredient'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.patch(`/recipes/${id}`, { status: 'ARCHIVED' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipe', id] });
      toast.success('Recipe archived');
    },
    onError: () => toast.error('Failed to archive recipe'),
  });

  const products: RawMaterialOption[] = productsData?.data ?? [];

  if (isLoading) return (
    <div className="space-y-6 page-enter">
      <Button variant="ghost" className="gap-2" onClick={() => navigate('/manufacturing/recipes')}>
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>
      <SkeletonTable rows={6} cols={4} />
    </div>
  );

  if (!recipe) return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <PackageOpen className="h-12 w-12 text-muted-foreground/40 mb-4" />
      <p className="font-medium text-muted-foreground">Recipe not found</p>
      <Button className="mt-4" onClick={() => navigate('/manufacturing/recipes')}>Back to Recipes</Button>
    </div>
  );

  const items: RecipeItem[] = recipe.items ?? [];
  // Use stored lineTotal from DB (seeded + computed on create/update)
  const totalCost = items.reduce((sum: number, i: RecipeItem) =>
    sum + Number(i.lineTotal ?? (Number(i.quantity ?? 0) * Number(i.standardCost ?? i.rawMaterial?.costPrice ?? 0))), 0);

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate('/manufacturing/recipes')}>
          <ArrowLeft className="h-4 w-4" /> Recipes
        </Button>
      </div>

      <PageHeader
        title={recipe.name}
        subtitle={`Version ${recipe.version} · Yield: ${recipe.yieldQuantity} ${recipe.yieldUOM}`}
        action={
          <div className="flex gap-2 flex-wrap">
            {canWrite && recipe.status !== 'ARCHIVED' && (
              <>
                <Button variant="outline" className="gap-2" onClick={() => navigate(`/manufacturing/production?recipe=${id}`)}>
                  <Factory className="h-4 w-4" /> Schedule Run
                </Button>
                <Button variant="outline" className="gap-2 text-destructive-text border-destructive/40 hover:bg-destructive/5"
                  onClick={() => archiveMutation.mutate()}>
                  <Archive className="h-4 w-4" /> Archive
                </Button>
              </>
            )}
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Status</p>
            <span className={cn('text-sm font-semibold px-2.5 py-1 rounded-full', STATUS_COLORS[recipe.status] ?? '')}>{recipe.status}</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Finished Good</p>
            <p className="text-sm font-semibold truncate">{recipe.finishedProduct?.name ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Material Cost</p>
            <p className="text-sm font-semibold">{formatCurrency(totalCost)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Cost / Unit</p>
            <p className="text-sm font-semibold">{formatCurrency(Number(recipe.costPerUnit ?? 0))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Ingredients table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> Bill of Materials
            <span className="text-xs font-normal text-muted-foreground ms-1">({items.length} ingredient{items.length !== 1 ? 's' : ''})</span>
          </CardTitle>
          {canWrite && recipe.status !== 'ARCHIVED' && (
            <Button size="sm" className="gap-1 h-8" onClick={() => setAddItemOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> Add Ingredient
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <PackageOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No ingredients yet. Add raw materials to define the bill of materials.</p>
            </div>
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('tblRawMaterial')}</Th>
                  <Th className="text-end">{t('tblQuantity')}</Th>
                  <Th>{t('tblUOM')}</Th>
                  <Th className="text-end">{t('tblCostUnit')}</Th>
                  <Th className="text-end">{t('tblLineCost')}</Th>
                  <Th>{t('tblNotes')}</Th>
                  {canWrite && recipe.status !== 'ARCHIVED' && (
                    <Th />
                  )}
                </Tr>
              </THead>
              <TBody>
                {items.map((item: RecipeItem) => {
                  // API returns item.rawMaterial (the related Product via rawMaterialId)
                  const product = item.rawMaterial ?? item.rawMaterialProduct ?? item.product;
                  // Prefer stored standardCost + lineTotal from DB
                  const costPerUnit = Number(item.standardCost ?? product?.costPrice ?? 0);
                  const lineCost = Number(item.lineTotal ?? (Number(item.quantity ?? 0) * costPerUnit));
                  return (
                    <Tr key={item.id}>
                      <Td label={t('tblRawMaterial')} className="text-sm font-medium">{product?.name ?? item.rawMaterial?.name ?? '—'}</Td>
                      <Td label={t('tblQuantity')} className="text-sm text-end font-mono">{Number(item.quantity).toFixed(3)}</Td>
                      <Td label={t('tblUOM')} className="text-sm text-muted-foreground">{item.uom}</Td>
                      <Td label={t('tblCostUnit')} className="text-sm text-end">{formatCurrency(costPerUnit)}</Td>
                      <Td label={t('tblLineCost')} className="text-sm text-end font-semibold">{formatCurrency(lineCost)}</Td>
                      <Td label={t('tblNotes')} className="text-xs text-muted-foreground max-w-[180px] truncate">{item.notes ?? '—'}</Td>
                      {canWrite && recipe.status !== 'ARCHIVED' && (
                        <Td>
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => { setEditingItem(item); setItemForm({ productId: item.rawMaterialProductId ?? item.productId ?? '', quantity: String(item.quantity), uom: item.uom ?? '', notes: item.notes ?? '' }); }}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive-text hover:text-destructive-text"
                              onClick={() => removeItemMutation.mutate(item.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Td>
                      )}
                    </Tr>
                  );
                })}
                <Tr hover={false} className="bg-muted/20 !border-t-2">
                  <Td colSpan={4} className="text-sm font-semibold text-end text-muted-foreground">{t('tblTotalMaterialCost')}</Td>
                  <Td className="text-sm font-bold text-end">{formatCurrency(totalCost)}</Td>
                  <Td colSpan={canWrite ? 2 : 1} className="td-hide" />
                </Tr>
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Ingredient Dialog */}
      <Dialog
        visible={addItemOpen}
        onHide={() => setAddItemOpen(false)}
        header="Add Ingredient"
        modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '40vw' }}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAddItemOpen(false)}><X className="h-4 w-4 me-1" />Cancel</Button>
            <Button onClick={() => addItemMutation.mutate({ productId: itemForm.productId, quantity: Number(itemForm.quantity), uom: itemForm.uom || undefined, notes: itemForm.notes || undefined })}
              disabled={addItemMutation.isPending}>
              <Save className="h-4 w-4 me-1" />{addItemMutation.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-2">
          <div>
            <Label className="mb-1 block">Raw Material *</Label>
            <Dropdown
              className="w-full"
              value={itemForm.productId}
              onChange={e => setItemForm(f => ({ ...f, productId: e.value }))}
              options={[
                { label: '-- Select product --', value: '' },
                ...products.map((p: RawMaterialOption) => ({ label: `${p.name} (Stock: ${p.stockQuantity})`, value: p.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder="-- Select product --"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block">Quantity *</Label>
              <Input type="number" min="0.001" step="0.001" value={itemForm.quantity}
                onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block">UOM</Label>
              <Input placeholder="e.g. KG, Pcs" value={itemForm.uom}
                onChange={e => setItemForm(f => ({ ...f, uom: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="mb-1 block">Notes</Label>
            <Input placeholder="Optional" value={itemForm.notes}
              onChange={e => setItemForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </Dialog>

      {/* Edit Ingredient Dialog */}
      <Dialog
        visible={!!editingItem}
        onHide={() => setEditingItem(null)}
        header="Edit Ingredient"
        modal
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        style={{ width: '40vw' }}
        footer={
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditingItem(null)}><X className="h-4 w-4 me-1" />Cancel</Button>
            <Button onClick={() => { if (editingItem) updateItemMutation.mutate({ itemId: editingItem.id, body: { quantity: Number(itemForm.quantity), uom: itemForm.uom || undefined, notes: itemForm.notes || undefined } }); }}
              disabled={updateItemMutation.isPending}>
              <Save className="h-4 w-4 me-1" />{updateItemMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block">Quantity *</Label>
              <Input type="number" min="0.001" step="0.001" value={itemForm.quantity}
                onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block">UOM</Label>
              <Input value={itemForm.uom}
                onChange={e => setItemForm(f => ({ ...f, uom: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="mb-1 block">Notes</Label>
            <Input value={itemForm.notes}
              onChange={e => setItemForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
